import React, { useState } from 'react';
import { config } from '../../config';
import ministryChildren from '../../assets/ministry-children.jpg';
import ministryYouth from '../../assets/ministry-youth.jpg';
import ministryWomen from '../../assets/ministry-women.jpg';
import ministryMen from '../../assets/ministry-men.jpg';
import ministryOutreach from '../../assets/ministry-outreach.jpg';
import ministryChoir from '../../assets/ministry-choir.jpg';

export const Ministries = () => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [selectedMinistry, setSelectedMinistry] = useState('');

  const ministries = [
    { name: "Children's Ministry", image: ministryChildren, desc: "Nurturing the next generation in the way of the Lord." },
    { name: "Youth Ministry", image: ministryYouth, desc: "Empowering young people to live for Christ." },
    { name: "Women's Fellowship", image: ministryWomen, desc: "A community for women to grow and support each other." },
    { name: "Men's Fellowship", image: ministryMen, desc: "Building strong men of faith and integrity." },
    { name: "Outreach Ministry", image: ministryOutreach, desc: "Spreading the love of Christ to our community." },
    { name: "Choir Ministry", image: ministryChoir, desc: "Leading the congregation in uplifting worship." }
  ];

  const handleRegisterClick = (ministryName: string) => {
    setSelectedMinistry(ministryName);
    setIsFormVisible(true);
    setTimeout(() => {
      document.getElementById('ministry-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <section id="ministries" className="section-padding bg-accent">
      <div className="container-custom">
        <div className="text-center mb-24">
          <p className="text-primary font-bold tracking-[0.3em] uppercase text-xs mb-6">Get Involved</p>
          <h2 className="text-4xl sm:text-6xl font-bold mb-8">Our Ministries</h2>
          <p className="text-lg text-secondary/60 max-w-2xl mx-auto">
            Find your place in our church family and serve with your unique gifts.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-32">
          {ministries.map((ministry, index) => (
            <div key={index} className="group relative aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-700">
              <img src={ministry.image} alt={ministry.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
              <div className="absolute inset-0 p-10 flex flex-col justify-end">
                <h3 className="text-2xl font-bold text-white mb-3">{ministry.name}</h3>
                <p className="text-white/70 text-sm mb-8 transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  {ministry.desc}
                </p>
                <button 
                  onClick={() => handleRegisterClick(ministry.name)}
                  className="w-fit px-6 py-3 bg-primary text-white text-xs font-bold uppercase tracking-widest rounded-full transform translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 delay-100"
                >
                  Join Ministry
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Registration Form */}
        {isFormVisible && (
          <div id="ministry-form" className="relative z-20 max-w-4xl mx-auto bg-white p-10 sm:p-20 rounded-[3rem] shadow-2xl border border-secondary/5 animate-fade-in-up">
            <div className="flex justify-between items-center mb-12">
              <div>
                <h3 className="text-3xl font-bold mb-2">Join a Ministry</h3>
                <p className="text-secondary/40 text-sm">Fill out the form below to get started.</p>
              </div>
              <button onClick={() => setIsFormVisible(false)} className="p-2 hover:text-primary transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form action={config.formspree_endpoint} method="POST" className="space-y-8">
              <div className="grid sm:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-secondary/40">Full Name</label>
                  <input type="text" name="name" required className="w-full bg-accent px-6 py-4 rounded-2xl border border-secondary/5 focus:border-primary outline-none transition-all" placeholder="Your name" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-secondary/40">Email Address</label>
                  <input type="email" name="email" required className="w-full bg-accent px-6 py-4 rounded-2xl border border-secondary/5 focus:border-primary outline-none transition-all" placeholder="your@email.com" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-secondary/40">Phone Number</label>
                  <input type="tel" name="phone" required className="w-full bg-accent px-6 py-4 rounded-2xl border border-secondary/5 focus:border-primary outline-none transition-all" placeholder="Your phone" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-secondary/40">Select Ministry</label>
                  <select
                    name="ministry" 
                    required 
                    value={selectedMinistry}
                    onChange={(e) => setSelectedMinistry(e.target.value)}
                    className="w-full bg-white text-secondary px-6 py-4 rounded-2xl border border-secondary/20 focus:border-primary outline-none transition-all"
                  >
                    <option value="">Choose a ministry</option>
                    {ministries.map(m => <option key={m.name} value={m.name}>{m.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-secondary/40">Why do you want to join?</label>
                <textarea name="message" rows={4} className="w-full bg-accent px-6 py-4 rounded-2xl border border-secondary/5 focus:border-primary outline-none transition-all resize-none" placeholder="Tell us a bit about yourself..."></textarea>
              </div>
              <button type="submit" className="btn-primary w-full">Submit Application</button>
            </form>
          </div>
        )}
      </div>
    </section>
  );
};
